import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../../api/client';
import BenchmarksEditor from './BenchmarksEditor';
import { toBenchmarkPayload } from './benchmarkLabels';

export default function BenchmarksPanel({ jobDescription }) {
  const jdId = jobDescription?.id;
  const isPractice = jobDescription?.jd_type === 'practice' || jobDescription?.is_practice;
  const [status, setStatus] = useState(jobDescription?.benchmarks_status || 'off');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    if (!jdId || isPractice) return;
    setLoading(true);
    setError('');
    try {
      const rubric = await api.get(`job-descriptions/${jdId}/benchmarks/`);
      setStatus(rubric.data.benchmarks_status || 'off');
      setRows(rubric.data.benchmarks || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load interview benchmarks.');
    } finally {
      setLoading(false);
    }
  }, [jdId, isPractice]);

  useEffect(() => {
    load();
  }, [load]);

  const persist = async (nextRows) => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await api.put(`job-descriptions/${jdId}/benchmarks/`, {
        benchmarks: toBenchmarkPayload(nextRows),
      });
      setRows(res.data.benchmarks || []);
      setStatus(res.data.benchmarks_status || 'draft');
      setMessage('Draft saved.');
    } catch (err) {
      setError(typeof err.response?.data?.error === 'string' ? err.response.data.error : 'Could not save interview benchmarks.');
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.put(`job-descriptions/${jdId}/benchmarks/`, {
        benchmarks: toBenchmarkPayload(rows),
      });
      const res = await api.post(`job-descriptions/${jdId}/benchmarks/approve/`);
      setRows(res.data.benchmarks || []);
      setStatus(res.data.benchmarks_status);
      setMessage('Approved. New interviews for this job will use these benchmarks.');
    } catch (err) {
      const detail = err.response?.data?.error;
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail || 'Could not approve interview benchmarks.'));
    } finally {
      setSaving(false);
    }
  };

  if (isPractice) return null;

  return (
    <div>
      {error ? <div className="text-sm text-red-600 mb-2 px-1">{error}</div> : null}
      {message ? <div className="text-sm text-emerald-700 mb-2 px-1">{message}</div> : null}
      <BenchmarksEditor
        mode="persisted"
        jdId={jdId}
        jdFields={{
          title: jobDescription?.title,
          description: jobDescription?.description,
          required_skills: jobDescription?.required_skills,
          nice_to_have_skills: jobDescription?.nice_to_have_skills,
          experience_level: jobDescription?.experience_level,
          interview_style: jobDescription?.interview_style,
        }}
        interviewStyle={jobDescription?.interview_style}
        rows={rows}
        onChange={setRows}
        status={status}
        saving={saving}
        loading={loading}
        onSaveDraft={() => persist(rows)}
        onApprove={approve}
      />
    </div>
  );
}
